"use client";

import React from "react";
import { Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { ExpandableTicketDetails } from "../ExpandableTicketDetails";
import type { SimpleFieldConfig } from "../../../UniversalCRMTable/types";

interface FieldRowDisplayProps {
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  value: unknown;
  canEdit: boolean;
  isFieldEditing: boolean;
  onEdit: (fieldId: string) => void;
  onCopy: () => void;
  renderDisplay: (fc: SimpleFieldConfig, v: unknown, fId?: string) => React.ReactNode;
}

export function FieldRowDisplay({
  fieldId,
  fieldConfig,
  value,
  canEdit,
  isFieldEditing,
  onEdit,
  onCopy,
  renderDisplay,
}: FieldRowDisplayProps) {
  const editable = canEdit && fieldConfig.editable !== false;

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            role="button"
            tabIndex={0}
            onClick={() => (editable ? onEdit(fieldId) : undefined)}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && editable) {
                e.preventDefault();
                onEdit(fieldId);
              }
            }}
            className={cn(
              "group p-2.5 sm:p-3 rounded-lg border border-gray-200 dark:border-gray-700 transition-all duration-200 shadow-sm",
              editable && "hover:bg-card hover:border-gray-300 dark:hover:border-gray-700 cursor-pointer hover:shadow-md"
            )}
          >
            <div className="flex items-start gap-2">
              <div className="flex-1 break-words overflow-hidden w-full">
                <div
                  className={cn(
                    "text-sm text-card-foreground leading-relaxed whitespace-pre-wrap break-words word-break-break-word",
                    (fieldConfig.type === "textarea" ||
                      fieldId.includes("description") ||
                      fieldId.includes("notes") ||
                      fieldId.includes("content")) &&
                      "py-0.5"
                  )}
                >
                  {renderDisplay(fieldConfig, value, fieldId)}
                </div>
              </div>
              {/* Edit affordance: compact icon (replaces the always-on "Tap to edit"
                  line that doubled every field's height on mobile). Desktop reveals
                  on hover; on touch the whole card is tappable. */}
              {editable && (
                <Pencil
                  className="h-3.5 w-3.5 mt-0.5 text-gray-300 dark:text-gray-600 flex-shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  aria-hidden="true"
                />
              )}
            </div>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="z-[10001]">
          <ContextMenuItem onSelect={onCopy}>Copy value</ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
      {(fieldId === "ticket_id" || fieldId.includes("ticket")) && !isFieldEditing && (
        <div className="mt-2 ml-0">
          <ExpandableTicketDetails
            ticketId={String(
              typeof value === "object" && value !== null && "id" in value
                ? (value as { id: string }).id
                : value
            )}
          />
        </div>
      )}
    </>
  );
}
